/**
 * Single source of truth for diagnostic receipt "status" semantics.
 *
 * The OPD receipt list (`/opd/diagnostic-receipts`) and the patient profile
 * Billing tab list the same receipts, so they must offer the same status
 * filters and render the same badges. Keeping the vocabulary here stops the two
 * pages from drifting apart (the patient view previously only knew the workflow
 * status and dropped the paid / due payment state entirely).
 *
 * Two different things are called "status" on a receipt:
 *  - `status`         the workflow state stored on the row (ACTIVE, USED, ...)
 *  - `paymentStatus`  a derived paid / due state from Paid + Due
 */

/** Workflow status values stored on `receipt.status`. */
export const DIAGNOSTIC_RECEIPT_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'USED', label: 'Used' },
  { value: 'PARTIALLY_USED', label: 'Partially Used' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'RISK_FUND', label: 'Risk Fund' },
] as const;

export type DiagnosticReceiptStatus =
  (typeof DIAGNOSTIC_RECEIPT_STATUS_OPTIONS)[number]['value'];

export const isDiagnosticReceiptStatus = (
  value?: string | null,
): value is DiagnosticReceiptStatus =>
  DIAGNOSTIC_RECEIPT_STATUS_OPTIONS.some((option) => option.value === value);

// Any other status falls back to the warning tone, mirroring the receipt list.
const RECEIPT_STATUS_BADGE: Record<string, string> = {
  ACTIVE: 'bg-success/10 text-success',
  USED: 'bg-primary/10 text-primary',
  REFUNDED: 'bg-danger/10 text-danger',
  RISK_FUND: 'bg-danger/10 text-danger',
};

/** Tailwind classes for the workflow status pill. */
export const diagnosticReceiptStatusClass = (status?: string | null) =>
  RECEIPT_STATUS_BADGE[String(status ?? '').toUpperCase()] ??
  'bg-warning/10 text-warning';

/** Human label for a stored workflow status (`RISK_FUND` → `Risk Fund`). */
export const diagnosticReceiptStatusLabel = (status?: string | null): string => {
  const value = String(status ?? '').toUpperCase();
  const option = DIAGNOSTIC_RECEIPT_STATUS_OPTIONS.find(
    (entry) => entry.value === value,
  );
  return option?.label ?? (value ? value.replace(/_/g, ' ') : '—');
};

/**
 * Payment state derived from Paid Amount + Due. There are only two states:
 * `paid` when nothing is outstanding and `due` otherwise. A partly paid receipt
 * is still a due receipt — the counter chases it exactly like one with nothing
 * paid — so the two are deliberately not distinguished.
 */
export type DiagnosticPaymentStatus = 'paid' | 'due' | 'risk_fund';

/**
 * Payment-status FILTER values. Adds `overdue` (money still owed past its
 * promised due date) on top of the two states a row can be in. `overdue` is a
 * view-only refinement — a row's badge stays `due`/`paid`.
 */
export type DiagnosticPaymentFilter = DiagnosticPaymentStatus | 'overdue';

export const DIAGNOSTIC_PAYMENT_STATUS_OPTIONS = [
  { value: 'paid', label: 'Paid' },
  { value: 'due', label: 'Due' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'risk_fund', label: 'Risk Fund' },
] as const;

export const isDiagnosticPaymentStatus = (
  value?: string | null,
): value is DiagnosticPaymentFilter =>
  DIAGNOSTIC_PAYMENT_STATUS_OPTIONS.some((option) => option.value === value);

/**
 * Normalises a payment-status value coming from the URL or an older link.
 * `partial` used to be a separate state and now simply means `due`.
 */
export const diagnosticPaymentStatusFilter = (
  value?: string | null,
): DiagnosticPaymentFilter | '' => {
  if (value === 'partial') return 'due';
  return isDiagnosticPaymentStatus(value) ? value : '';
};

export const diagnosticPaymentStatusOf = (receipt?: {
  status?: string | null;
  paidAmount?: number | string | null;
  dueAmount?: number | string | null;
} | null): DiagnosticPaymentStatus => {
  // A written-off receipt has no due, but calling it "Paid" would claim money
  // that was never collected.
  if (String(receipt?.status ?? '').toUpperCase() === 'RISK_FUND') {
    return 'risk_fund';
  }
  return (Number(receipt?.dueAmount) || 0) > 0 ? 'due' : 'paid';
};

export const DIAGNOSTIC_PAYMENT_STATUS_META: Record<
  DiagnosticPaymentStatus,
  { label: string; className: string }
> = {
  paid: { label: 'Paid', className: 'bg-success/10 text-success' },
  due: { label: 'Due', className: 'bg-danger/10 text-danger' },
  risk_fund: { label: 'Risk Fund', className: 'bg-warning/10 text-warning' },
};
